/**
 * LightboxProvider.tsx — SF v8 Cycle 69
 *
 * Lightweight context provider — wraps the page, holds open/close state,
 * and dynamically imports LightboxOverlay only when the lightbox is triggered.
 *
 * Before this split: LightboxProvider + LightboxOverlay + all FLIP helpers
 * lived in a single Lightbox.tsx. Even with ssr:false on the page-level import
 * the entire chunk (FLIP math, keyboard handlers, image refs) landed in the
 * initial JS bundle because LightboxProvider was a regular import.
 *
 * After this split:
 *   - LightboxProvider.tsx  — tiny, regular import (context + state machine)
 *   - LightboxOverlay.tsx   — heavy, loaded via next/dynamic only on first open
 *
 * Result: LightboxOverlay chunk deferred until user clicks an image.
 * Zero visual change, zero functional change.
 *
 * Also exports LightboxTrigger (lightweight click wrapper, fine as regular import).
 *
 * SOP refs: SOP-WEBSITE-03 rule 12 (third-party heavy UI loaded on interaction),
 *           FB-9d1e6c (no img-loaded SVG), SOP-WEBSITE-02 (no CLS regression).
 */

'use client';

import dynamic from 'next/dynamic';
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

import type { LightboxState } from './LightboxOverlay';

// ── Dynamic import — LightboxOverlay only loads when lightboxState !== null ──

const LightboxOverlayDynamic = dynamic(
  () => import('./LightboxOverlay').then(m => ({ default: m.LightboxOverlay })),
  { ssr: false }
);

// ── Context ───────────────────────────────────────────────────────────────────

interface LightboxContextValue {
  openLightbox: (src: string, alt: string, rect: DOMRect) => void;
}

const LightboxContext = createContext<LightboxContextValue | null>(null);

export function useLightbox(): LightboxContextValue {
  const ctx = useContext(LightboxContext);
  if (!ctx) throw new Error('useLightbox must be used inside <LightboxProvider>');
  return ctx;
}

// ── LightboxProvider ──────────────────────────────────────────────────────────

export function LightboxProvider({ children }: { children: React.ReactNode }) {
  const [lightboxState, setLightboxState] = useState<LightboxState | null>(null);

  const openLightbox = useCallback((src: string, alt: string, rect: DOMRect) => {
    setLightboxState({ src, alt, rect });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxState(null);
  }, []);

  return (
    <LightboxContext.Provider value={{ openLightbox }}>
      {children}
      {lightboxState && (
        <LightboxOverlayDynamic state={lightboxState} onClose={closeLightbox} />
      )}

      {/* Scoped CSS — co-located with component (no cascade leakage) */}
      <style>{`
/* ── SF v8 Lightbox overlay ────────────────────────────────────────── */
.sf8-lightbox-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(14, 10, 24, 0.92); /* --bg-dark at 92% per brief */
  display: flex;
  align-items: center;
  justify-content: center;
}

.sf8-lightbox-img {
  max-width: 85vw;
  max-height: 85vh;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  transform-origin: center center;
  /* transition applied inline — varies by phase */
}

.sf8-lightbox-close {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1;
  background: rgba(244, 240, 234, 0.12);
  border: 1px solid rgba(244, 240, 234, 0.20);
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(244, 240, 234, 0.90);
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease;
}
.sf8-lightbox-close:hover {
  background: rgba(244, 240, 234, 0.22);
  color: #fff;
}
.sf8-lightbox-close:focus-visible {
  outline: 2px solid var(--gold);
  outline-offset: 3px;
}

/* ── Lightbox trigger wrapper ──────────────────────────────────────── */
.sf8-lightbox-trigger {
  display: block;         /* preserves surrounding block flow */
  cursor: zoom-in;
  /* No other style overrides — wrapper sits around existing elements */
}
.sf8-lightbox-trigger:focus-visible {
  outline: 2px solid var(--gold);
  outline-offset: 3px;
  border-radius: 2px;
}

/* Touch — swipe-down to close handled in JS */
@media (prefers-reduced-motion: reduce) {
  .sf8-lightbox-overlay { transition: none !important; }
  .sf8-lightbox-img { transition: none !important; }
}
      `}</style>
    </LightboxContext.Provider>
  );
}

// ── LightboxTrigger — wraps any child image element ──────────────────────────

interface TriggerProps {
  /** The full-res src to show in the lightbox (usually same as thumbnail src) */
  src: string;
  alt: string;
  children: React.ReactNode;
}

export function LightboxTrigger({ src, alt, children }: TriggerProps) {
  const { openLightbox } = useLightbox();
  const wrapRef = useRef<HTMLDivElement>(null);

  function open() {
    const el = wrapRef.current?.querySelector('img') ?? wrapRef.current;
    const rect = el?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0);
    openLightbox(src, alt, rect);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  }

  return (
    <div
      ref={wrapRef}
      className="sf8-lightbox-trigger"
      role="button"
      tabIndex={0}
      aria-haspopup="dialog"
      aria-label={`Enlarge image: ${alt}`}
      onClick={open}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
}
